/**
 * Constants for the Eight Shell 3D Visualization
 * All measurements in meters unless otherwise specified
 */

// =============================================================================
// BOAT DIMENSIONS
// =============================================================================

export const BOAT_DIMENSIONS = {
  /** Length of racing shell in meters (typical eight: 62 feet) */
  HULL_LENGTH: 18.9,
  /** Beam at waterline in meters (typical eight: 23 inches) */
  HULL_BEAM_WATERLINE: 0.59,
  /** Depth below waterline in meters (reduced from 0.6m for visibility) */
  DRAFT: 0.15,
  /** Height of hull above waterline in meters (freeboard) */
  HULL_HEIGHT_ABOVE_WATER: 0.3,
  /** Number of hull segments for geometry (higher = smoother taper) */
  HULL_SEGMENTS: 40,
  /** Percentage of hull length where bow taper starts (0.15 = 15%) */
  BOW_TAPER_START: 0.15,
  /** Percentage of hull length where stern taper starts (0.85 = 85%) */
  STERN_TAPER_START: 0.85,
  /** Distance before stern taper where stroke seat is positioned (meters) */
  STROKE_SEAT_OFFSET: 0.5,
} as const;

// =============================================================================
// CREW CONFIGURATION
// =============================================================================

export const CREW_CONFIG = {
  /** Number of rowers in the boat */
  NUM_ROWERS: 8,
  /** Multiplier for coxswain position relative to seat spacing */
  COXSWAIN_POSITION_MULTIPLIER: 0.6,
} as const;

// =============================================================================
// ROWER DIMENSIONS
// =============================================================================

export const ROWER_DIMENSIONS = {
  // Seat
  /** Seat width in meters */
  SEAT_WIDTH: 0.35,
  /** Seat depth (front-to-back) in meters */
  SEAT_DEPTH: 0.25,
  /** Seat height in meters */
  SEAT_HEIGHT: 0.08,
  /** Height of seat above deck in meters */
  SEAT_Y_OFFSET: 0.05,
  /** Distance from seat to rigger along boat length (negative = toward bow) */
  SEAT_TO_RIGGER_OFFSET: -0.4,

  // Body
  /** Shoulder width in meters */
  TORSO_WIDTH: 0.45,
  /** Torso depth (front-to-back) in meters */
  TORSO_DEPTH: 0.25,
  /** Seated torso height in meters (from seat to neck/shoulders) */
  TORSO_HEIGHT: 0.68,
  /** Head radius in meters */
  HEAD_RADIUS: 0.11,

  // Sphere detail (number of segments)
  /** Horizontal segments for head sphere */
  HEAD_SEGMENTS_WIDTH: 16,
  /** Vertical segments for head sphere */
  HEAD_SEGMENTS_HEIGHT: 16,
} as const;

// =============================================================================
// COXSWAIN DIMENSIONS (smaller than rowers)
// =============================================================================

export const COXSWAIN_DIMENSIONS = {
  /** Narrower shoulder width in meters */
  TORSO_WIDTH: 0.35,
  /** Smaller torso depth in meters */
  TORSO_DEPTH: 0.2,
  /** Shorter seated torso height in meters (~75% of rower height) */
  TORSO_HEIGHT: 0.51,
  /** Smaller head radius in meters */
  HEAD_RADIUS: 0.09,
  /** Seat height in meters */
  SEAT_HEIGHT: 0.06,
  /** Seat dimensions in meters */
  SEAT_SIZE: 0.3,
  /** Vertical offset below deck (negative = lower in boat) */
  SEAT_Y_OFFSET: -0.05,
} as const;

// =============================================================================
// RIGGER DIMENSIONS
// =============================================================================

export const RIGGER_DIMENSIONS = {
  /** Distance from hull centerline to oarlock in meters */
  RIGGER_LENGTH: 0.8,
  /** Height of rigger above deck in meters */
  RIGGER_HEIGHT: 0.15,
  /** Rigger beam width in meters */
  RIGGER_WIDTH: 0.08,
  /** Rigger beam height in meters */
  RIGGER_BEAM_HEIGHT: 0.04,

  // Oarlock/Gate
  /** Gate width in meters */
  GATE_WIDTH: 0.08,
  /** Gate height in meters */
  GATE_HEIGHT: 0.06,
  /** Gate depth in meters */
  GATE_DEPTH: 0.08,
} as const;

// =============================================================================
// OAR DIMENSIONS
// =============================================================================

export const OAR_DIMENSIONS = {
  /** Total oar length in meters (sweep oar) */
  OAR_LENGTH: 3.7,
  /** Distance from oarlock to handle in meters (inboard) */
  OAR_INBOARD: 1.2,
  /** Shaft diameter in meters */
  SHAFT_DIAMETER: 0.025,
  /** Number of segments for shaft cylinder */
  SHAFT_SEGMENTS: 12,

  // Handle
  /** Handle grip radius in meters */
  HANDLE_RADIUS: 0.04,
  /** Handle grip length in meters (fits two hands) */
  HANDLE_LENGTH: 0.3,
  /** Handle cylinder segments */
  HANDLE_SEGMENTS: 12,

  // Blade
  /** Blade thickness in meters */
  BLADE_THICKNESS: 0.04,
  /** Blade height (vertical when squared) in meters */
  BLADE_HEIGHT: 0.65,
  /** Blade width in meters */
  BLADE_WIDTH: 0.2,
} as const;

// =============================================================================
// ANIMATION CONFIGURATION
// =============================================================================

export const ANIMATION_CONFIG = {
  /** Duration of one complete stroke cycle in seconds */
  STROKE_DURATION: 1.5,
  /** Percentage of cycle for drive phase (0.4 = 40%) */
  DRIVE_PHASE_RATIO: 0.4,
  /** Percentage of cycle for recovery phase (0.6 = 60%) */
  RECOVERY_PHASE_RATIO: 0.6,

  // Oar sweep angles (degrees)
  /** Oar angle at catch position (degrees) */
  OAR_CATCH_ANGLE: -45,
  /** Oar angle at finish position (degrees) */
  OAR_FINISH_ANGLE: 45,

  // Oar cone kinematics
  /** Cone tilt angle from vertical Y-axis (degrees) - controls how far oar tips swing */
  OAR_CONE_TILT: 30,

  // Body lean angles (degrees)
  /** Body lean at catch (forward lean, degrees) */
  BODY_CATCH_ANGLE: -15,
  /** Body lean at finish (back lean, degrees) */
  BODY_FINISH_ANGLE: 15,
} as const;

// =============================================================================
// COLORS
// =============================================================================

export const COLORS = {
  // Scene
  /** Background color for canvas */
  BACKGROUND: '#f6f7fb',
  /** Water surface color (lighter blue) */
  WATER: '#7FC0FF',
  /** Water opacity (0-1) */
  WATER_OPACITY: 0.6,

  // Boat
  /** Hull color (bright Empacher yellow) */
  HULL: '#FFE347',

  // Crew
  /** Even seat jersey color - Port side (red) */
  JERSEY_EVEN: '#dc2626',
  /** Odd seat jersey color - Starboard side (green) */
  JERSEY_ODD: '#16a34a',
  /** Skin tone for heads */
  SKIN_TONE: '#f4a582',
  /** Seat color (gray) */
  SEAT: '#4a5568',

  // Coxswain
  /** Coxswain seat color (purple) */
  COX_SEAT: '#6366f1',
  /** Coxswain uniform color (blue) */
  COX_UNIFORM: '#3b82f6',

  // Rigger
  /** Rigger beam color (dark gray) */
  RIGGER: '#2d3748',
  /** Oarlock/gate color (medium gray) */
  GATE: '#718096',

  // Oar
  /** Oar shaft color (black, carbon fiber) */
  OAR_SHAFT: '#000000',
  /** Oar handle color (grey grip) */
  OAR_HANDLE: '#d1d5db',
  /** Oar blade color (dark blue) */
  OAR_BLADE: '#1e3a8a',

  // Helpers
  /** Grid center line color */
  GRID_CENTER: '#888888',
  /** Grid line color */
  GRID_LINES: '#cccccc',
} as const;

// =============================================================================
// CAMERA & SCENE CONFIGURATION
// =============================================================================

export const CAMERA_CONFIG = {
  /** Camera Y position in meters */
  CAMERA_Y: 3,
  /** Field of view in degrees */
  FOV: 36,
  /** Near clipping plane in meters */
  NEAR: 0.1,
  /** Far clipping plane in meters */
  FAR: 120,
  /** Minimum zoom distance in meters */
  MIN_DISTANCE: 5,
  /** Maximum zoom distance in meters */
  MAX_DISTANCE: 100,
} as const;

export const LIGHTING_CONFIG = {
  /** Ambient light intensity (0-1) */
  AMBIENT_INTENSITY: 0.6,
  /** Directional light position [x, y, z] */
  DIRECTIONAL_POSITION: [12, 14, 8] as const,
  /** Directional light intensity (0-1) */
  DIRECTIONAL_INTENSITY: 0.85,
} as const;

export const SCENE_HELPERS = {
  /** Axes helper size in meters */
  AXES_SIZE: 12,
  /** Grid size in meters */
  GRID_SIZE: 40,
  /** Grid divisions */
  GRID_DIVISIONS: 40,
} as const;

export const CANVAS_CONFIG = {
  /** Canvas container width */
  WIDTH: '100%',
  /** Canvas container height in pixels */
  HEIGHT: '620px',
  /** Canvas container min-height in pixels */
  MIN_HEIGHT: '620px',
} as const;

// =============================================================================
// MATERIAL PROPERTIES (PBR - Physically Based Rendering)
// =============================================================================

export const MATERIAL_PROPERTIES = {
  // Hull - carbon fiber composite with soft sheen
  /** Hull material roughness (0-1) */
  HULL_ROUGHNESS: 0.08,
  /** Hull material metalness (0-1) */
  HULL_METALNESS: 0.05,

  // Riggers and Gates - metal with some wear
  /** Rigger material roughness (0-1) */
  RIGGER_ROUGHNESS: 0.25,
  /** Rigger material metalness (0-1) */
  RIGGER_METALNESS: 0.6,
  /** Gate material roughness (0-1) */
  GATE_ROUGHNESS: 0.3,
  /** Gate material metalness (0-1) */
  GATE_METALNESS: 0.5,

  // Seats and Bodies - matte plastic/fabric
  /** Seat material roughness (0-1) */
  SEAT_ROUGHNESS: 0.6,
  /** Seat material metalness (0-1) */
  SEAT_METALNESS: 0.0,

  // Oar Blades - painted fiberglass with semi-gloss
  /** Blade material roughness (0-1) */
  BLADE_ROUGHNESS: 0.35,
  /** Blade material metalness (0-1) */
  BLADE_METALNESS: 0.1,

  // Oar Shaft - carbon fiber
  /** Shaft material roughness (0-1) */
  SHAFT_ROUGHNESS: 0.4,
  /** Shaft material metalness (0-1) */
  SHAFT_METALNESS: 0.2,

  // Water
  /** Water surface scale multiplier */
  WATER_SURFACE_SCALE: 1.4,
  /** Water material roughness (0-1) */
  WATER_ROUGHNESS: 0.8,
  /** Water material metalness (0-1) */
  WATER_METALNESS: 0.05,
} as const;
